import React, { useMemo } from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { useFragment, graphql } from 'react-relay';
import type { SmartLinkResponse } from '@atlaskit/linking-types';
import { useGoalUrlConstructor } from '@atlassian/jira-issue-field-goals-common/src/index.tsx';
import { ItemWithEntryPointPopup } from '@atlassian/jira-item-with-popup/src/ui/item-with-entrypoint-popup/index.tsx';
import { SimpleInlineSmartLink } from '@atlassian/jira-issue-field-smart-link/src/ui/simple-inline-smart-link/index.tsx';
import { EmptyFieldText } from '@atlassian/jira-issue-empty-field-text/src/ui/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fg } from '@atlassian/jira-feature-gating';
import type { goals_issueFieldGoalsReadviewFull_GoalsReadView$key as GoalsFragment } from './__generated__/goals_issueFieldGoalsReadviewFull_GoalsReadView.graphql';
import type { GoalsReadViewProps, ValidNonNullableSelectedGoalNode } from './types.tsx';
import { goalsPopupContentEntryPoint } from './popup-content/entrypoint.tsx';
import messages from './messages.tsx';
import { generatePlaceholderData } from './generate-placeholder-data.tsx';

/**
 * The GoalsReadView will show a read only view of Goals field.
 * @param props [GoalsReadViewProps](./types.tsx)
 */
export const GoalsReadView = ({ fragmentRef, isTruncated }: GoalsReadViewProps) => {
	const { formatMessage } = useIntl();
	const constructGoalUrl = useGoalUrlConstructor();

	const { selectedGoals } = useFragment<GoalsFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment goals_issueFieldGoalsReadviewFull_GoalsReadView on JiraGoalsField
			@argumentDefinitions(
				isPlaceholderDataEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/goals-ssr-smart-card.relayprovider"
				}
			) {
				selectedGoals(first: 1000) {
					# eslint-disable-next-line @atlassian/relay/must-colocate-fragment-spreads
					...popupContent_issueFieldGoalsReadviewFull
					edges {
						node {
							id
							key
							name @include(if: $isPlaceholderDataEnabled)
							status @include(if: $isPlaceholderDataEnabled)
						}
					}
				}
			}
		`,
		fragmentRef,
	);

	const { firstGoal, totalGoalsCount } = useMemo(() => {
		const nodes: ValidNonNullableSelectedGoalNode[] =
			selectedGoals?.edges
				?.map((edge) => edge?.node)
				?.filter((node): node is ValidNonNullableSelectedGoalNode => Boolean(node?.key)) ?? [];

		const transformedNodes = nodes.map((node) => ({
			...node,
			key: node.id,
			url: constructGoalUrl({ ari: node.id, key: node.key }),
		}));

		return {
			firstGoal: transformedNodes[0],
			totalGoalsCount: transformedNodes.length,
		};
	}, [constructGoalUrl, selectedGoals?.edges]);

	const placeholderDataForSmartCard = useMemo<SmartLinkResponse | undefined>(() => {
		return firstGoal && fg('goals_ssr_smart_card')
			? generatePlaceholderData(firstGoal.url, firstGoal.name, firstGoal.status)
			: undefined;
	}, [firstGoal]);

	if (totalGoalsCount === 0 || !selectedGoals) {
		return <EmptyFieldText />;
	}

	return (
		<ItemWithEntryPointPopup
			ItemComponent={
				<SimpleInlineSmartLink
					anchorTarget="_blank"
					url={firstGoal.url}
					{...(fg('goals_ssr_smart_card') ? { placeholderData: placeholderDataForSmartCard } : {})}
				/>
			}
			totalItems={totalGoalsCount}
			isTruncated={isTruncated}
			entryPoint={goalsPopupContentEntryPoint}
			entryPointParams={{}}
			entryPointRuntimeProps={{ fragmentRef: selectedGoals }}
			showMoreTooltip={formatMessage(messages.showMoreTooltip)}
			actionSubjectIdPrefix="goals"
		/>
	);
};
